<script setup lang="ts">
import {
    computed, onMounted, ref,
} from 'vue';
import {
    BCard, BIcon, BSparkline,
} from '@/design-system';
import {
    formatNumberFull, getChangeIcon, getChangeColor,
} from '@/utils/formatUtils';
import type { DashboardChangeType } from '@/types/dashboard/DashboardTypes';

type BadgeType = {
    text: string;
    icon?: string;
    color?: string;
};

type PropsType = {
    title?: string;
    value?: string | number | null;
    icon?: string;
    iconColor?: string;
    iconBackgroundColor?: string;
    isLoading?: boolean;
    change?: DashboardChangeType;
    sparklineData?: number[];
    sparklineColor?: string;
    animateOnLoad?: boolean;
    clickable?: boolean;
    elevation?: number;
    badge?: BadgeType;
    progressValue?: number;
    progressColor?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    title: '',
    value: null,
    isLoading: false,
    animateOnLoad: true,
    clickable: false,
    elevation: 0,
});

const isValueAnimated = ref(false);

const cardClass = computed(() => {
    const classes = ['b-dashboard-stat-card'];

    if (props.clickable) {
        classes.push('dashboard-card-hover');
        classes.push('cursor-pointer');
    }

    return classes.join(' ');
});

const valueClass = computed(() => {
    const classes = ['b-dashboard-stat-card__value'];

    if (props.animateOnLoad && isValueAnimated.value) {
        classes.push('dashboard-animate-value');
    }

    return classes.join(' ');
});

const formattedValue = computed(() => {
    if (typeof props.value === 'string') {
        return props.value;
    }

    return formatNumberFull(props.value);
});

const changeIcon = computed(() => getChangeIcon(props.change?.type));

const changeColor = computed(() => getChangeColor(props.change?.type));

const effectiveSparklineColor = computed(() => props.sparklineColor || props.iconBackgroundColor || 'primary');

onMounted(() => {
    if (props.animateOnLoad && !props.isLoading) {
        isValueAnimated.value = true;
    }
});

defineOptions({ inheritAttrs: false });
</script>

<template>
    <BCard
        :class="cardClass"
        variant="flat"
        v-bind="$attrs"
    >
        <div
            v-if="isLoading"
            class="b-dashboard-stat-card__loading"
        >
            <div class="d-flex align-start justify-space-between mb-3 skeleton-header">
                <div class="dashboard-skeleton b-dashboard-stat-card__skeleton-title" />
                <div class="dashboard-skeleton b-dashboard-stat-card__skeleton-icon" />
            </div>
            <div class="dashboard-skeleton b-dashboard-stat-card__skeleton-value mb-2" />
            <div class="dashboard-skeleton b-dashboard-stat-card__skeleton-change mb-3" />
            <div class="dashboard-skeleton b-dashboard-stat-card__skeleton-sparkline" />
        </div>

        <div
            v-else
            class="b-dashboard-stat-card__content"
        >
            <div class="b-dashboard-stat-card__header">
                <div class="b-dashboard-stat-card__title">
                    {{ title }}
                </div>
                <div
                    v-if="icon"
                    class="b-dashboard-stat-card__icon-container"
                    :style="{
                        backgroundColor: `rgb(var(--v-theme-${iconBackgroundColor || 'primary'}), 0.1)`,
                    }"
                >
                    <BIcon
                        :icon="icon"
                        :color="iconColor || iconBackgroundColor || 'primary'"
                        size="small"
                    />
                </div>
            </div>

            <div :class="valueClass">
                {{ formattedValue }}
            </div>

            <div
                v-if="change"
                class="b-dashboard-stat-card__change"
            >
                <BIcon
                    v-if="changeIcon"
                    :icon="changeIcon"
                    :color="changeColor"
                    size="x-small"
                    class="mr-1"
                />
                <span
                    class="b-dashboard-stat-card__change-value"
                    :class="`text-${changeColor}`"
                >
                    {{ change.value }}
                </span>
                <span
                    v-if="change.period"
                    class="b-dashboard-stat-card__change-period"
                >
                    {{ change.period }}
                </span>
            </div>

            <div
                v-if="badge"
                class="b-dashboard-stat-card__badge"
            >
                <div
                    class="b-dashboard-stat-card__badge-chip"
                    :style="{
                        backgroundColor: `rgb(var(--v-theme-${badge.color || 'warning'}), 0.1)`,
                        color: `rgb(var(--v-theme-${badge.color || 'warning'}))`,
                    }"
                >
                    <BIcon
                        v-if="badge.icon"
                        :icon="badge.icon"
                        size="x-small"
                        class="mr-1"
                    />
                    <span class="b-dashboard-stat-card__badge-text">{{ badge.text }}</span>
                </div>
            </div>

            <div
                v-if="sparklineData && sparklineData.length > 0"
                class="b-dashboard-stat-card__sparkline"
            >
                <BSparkline
                    :data="sparklineData"
                    :color="effectiveSparklineColor"
                    :height="40"
                />
            </div>

            <div
                v-if="progressValue !== undefined"
                class="b-dashboard-stat-card__progress"
            >
                <div
                    class="b-dashboard-stat-card__progress-fill"
                    :style="{
                        width: `${Math.min(100, Math.max(0, progressValue))}%`,
                        backgroundColor: `rgb(var(--v-theme-${progressColor || iconBackgroundColor || 'primary'}))`,
                    }"
                />
            </div>
        </div>
    </BCard>
</template>

<style scoped lang="scss">
@use '@/design-system/styles/dashboard-animations';

.b-dashboard-stat-card {
    --content-padding-inline: 30px;
    --content-padding-block: 20px;
    border-radius: 8px;
    height: 100%;
    cursor: default;
    border: 1px solid rgb(204 225 235);
    transition: box-shadow 0.2s ease;
}

.b-dashboard-stat-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.b-dashboard-stat-card :deep(.v-card-text) {
    padding: 0;
    height: 100%;
}

.cursor-pointer {
    cursor: pointer;
}

.b-dashboard-stat-card__loading,
.b-dashboard-stat-card__content {
    padding: var(--content-padding-block) 0 0 0;
    display: flex;
    flex-flow: column nowrap;
    height: 100%;
}

.b-dashboard-stat-card__content > *:last-child:not(.b-dashboard-stat-card__sparkline) {
    padding-bottom: var(--content-padding-block);
    margin-bottom: 0;
}

.b-dashboard-stat-card__header {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    padding-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__icon-container {
    width: 30px;
    height: 30px;
    border-radius: 20%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.b-dashboard-stat-card__title {
    font-size: 12px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex: 1;
}

.b-dashboard-stat-card__value {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
    color: rgb(var(--v-theme-on-surface));
    margin-bottom: 8px;
    padding-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__change {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 12px;
    font-size: 13px;
    padding-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__change-value {
    font-weight: 600;
}

.b-dashboard-stat-card__change-period {
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-weight: 400;
    margin-left: 4px;
}

.b-dashboard-stat-card__badge {
    margin-bottom: 12px;
    padding-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__badge-chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.b-dashboard-stat-card__badge-text {
    line-height: 1;
}

.b-dashboard-stat-card__sparkline {
    margin-top: auto;
    display: flex;
}

.b-dashboard-stat-card__progress {
    margin-top: auto;
    height: 4px;
    background-color: rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 0 0 8px 8px;
    overflow: hidden;
}

.b-dashboard-stat-card__progress-fill {
    height: 100%;
    border-radius: 0 0 8px 8px;
    transition: width 0.6s ease;
}

.skeleton-header {
    padding-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__skeleton-icon {
    width: 30px;
    height: 30px;
    border-radius: 20%;
}

.b-dashboard-stat-card__skeleton-title {
    width: 80px;
    height: 12px;
}

.b-dashboard-stat-card__skeleton-value {
    width: 120px;
    height: 28px;
    margin-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__skeleton-change {
    width: 100px;
    height: 13px;
    margin-inline: var(--content-padding-inline);
}

.b-dashboard-stat-card__skeleton-sparkline {
    width: 100%;
    height: 40px;
}
</style>
